import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiDownload, FiX, FiUploadCloud } from 'react-icons/fi';

export default function InventoryList() {
  const [inventoryItems, setInventoryItems] = useState([
    {
      id: 1,
      itemName: 'Rice',
      quantity: '500 kg',
      branch: 'Main',
      commodity: 'Grains',
      whr: 'WH001',
      dateAdded: '2023-05-15',
      status: 'In Stock',
    },
    {
      id: 2,
      itemName: 'Wheat',
      quantity: '300 kg',
      branch: 'Secondary',
      commodity: 'Grains',
      whr: 'WH002',
      dateAdded: '2023-05-16',
      status: 'In Stock',
    },
    {
      id: 3,
      itemName: 'Sugar',
      quantity: '200 kg',
      branch: 'Main',
      commodity: 'Sweeteners',
      whr: 'WH001',
      dateAdded: '2023-05-17',
      status: 'Low Stock',
    },
    {
      id: 4,
      itemName: 'Salt',
      quantity: '150 kg',
      branch: 'Secondary',
      commodity: 'Condiments',
      whr: 'WH003',
      dateAdded: '2023-05-18',
      status: 'In Stock',
    },
    {
      id: 5,
      itemName: 'Cooking Oil',
      quantity: '100 L',
      branch: 'Main',
      commodity: 'Oils',
      whr: 'WH001',
      dateAdded: '2023-05-19',
      status: 'In Stock',
    },
    {
      id: 6,
      itemName: 'Lentils',
      quantity: '250 kg',
      branch: 'Secondary',
      commodity: 'Pulses',
      whr: 'WH002',
      dateAdded: '2023-05-20',
      status: 'In Stock',
    },
    {
      id: 7,
      itemName: 'Flour',
      quantity: '350 kg',
      branch: 'Main',
      commodity: 'Grains',
      whr: 'WH003',
      dateAdded: '2023-05-21',
      status: 'In Stock',
    },
  ]);

  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterCommodity, setFilterCommodity] = useState('');
  const [filterWHR, setFilterWHR] = useState('');
  const [showWHRModal, setShowWHRModal] = useState(false);
  const [whrForm, setWhrForm] = useState({
    state: '',
    commodity: '',
    warehouse: '',
    season: '',
    scheme: '',
    file: null,
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Filter inventory items based on search and filters
  const filtered = inventoryItems.filter(
    (item) =>
      (item.itemName.toLowerCase().includes(search.toLowerCase()) ||
        item.quantity.toLowerCase().includes(search.toLowerCase()) ||
        item.status.toLowerCase().includes(search.toLowerCase())) &&
      (filterBranch === '' || item.branch === filterBranch) &&
      (filterCommodity === '' || item.commodity === filterCommodity) &&
      (filterWHR === '' || item.whr === filterWHR)
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleWhrFormChange = (field, value) => {
    setWhrForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e) => {
    setWhrForm((prev) => ({
      ...prev,
      file: e.target.files[0],
    }));
  };

  const handleWhrSubmit = () => {
    console.log('WHR Form submitted:', whrForm);
    // Reset form and close modal
    setWhrForm({
      state: '',
      commodity: '',
      warehouse: '',
      season: '',
      scheme: '',
      file: null,
    });
    setShowWHRModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <h1 className="text-4xl font-extrabold text-blue-800 tracking-tight">
          📋 Inventory List
        </h1>
        <div className="flex gap-4">
          <button className="bg-green-600 hover:bg-green-700 text-white px-7 py-3 text-lg rounded-2xl shadow-lg transition-all duration-200 font-semibold flex items-center gap-2">
            <FiDownload className="text-xl" /> Export Excel
          </button>
          <button
            onClick={() => setShowWHRModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-3 text-lg rounded-2xl shadow-lg transition-all duration-200 font-semibold flex items-center gap-2"
          >
            <FiUploadCloud className="text-xl" /> Upload WHR
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-8 space-y-4">
        <input
          type="text"
          placeholder="🔍 Search inventory..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-5 py-3 text-lg border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Branch Filter */}
          <select
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            className="px-5 py-3 text-lg border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Branches</option>
            <option value="Main">Main</option>
            <option value="Secondary">Secondary</option>
          </select>

          {/* Commodity Filter */}
          <select
            value={filterCommodity}
            onChange={(e) => setFilterCommodity(e.target.value)}
            className="px-5 py-3 text-lg border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Commodities</option>
            <option value="Grains">Grains</option>
            <option value="Sweeteners">Sweeteners</option>
            <option value="Condiments">Condiments</option>
            <option value="Oils">Oils</option>
            <option value="Pulses">Pulses</option>
          </select>

          {/* WHR Filter */}
          <select
            value={filterWHR}
            onChange={(e) => setFilterWHR(e.target.value)}
            className="px-5 py-3 text-lg border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All WHR</option>
            <option value="WH001">WH001</option>
            <option value="WH002">WH002</option>
            <option value="WH003">WH003</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-2xl border bg-white"
      >
        <table className="w-full text-left border-collapse text-lg">
          <thead className="bg-blue-600 text-white">
            <tr>
              {[
                '#',
                'Item Name',
                'Quantity',
                'Branch',
                'Commodity',
                'WHR',
                'Date Added',
                'Status',
              ].map((head) => (
                <th
                  key={head}
                  className="py-4 px-6 text-base font-semibold tracking-wide border-b"
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((item, idx) => (
              <motion.tr
                key={item.id}
                className="border-b odd:bg-gray-50 even:bg-white hover:bg-blue-50 transition text-gray-800"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.05 }}
              >
                <td className="py-4 px-6">
                  {idx + 1 + (currentPage - 1) * itemsPerPage}
                </td>
                <td className="py-4 px-6 font-semibold">{item.itemName}</td>
                <td className="py-4 px-6">{item.quantity}</td>
                <td className="py-4 px-6">{item.branch}</td>
                <td className="py-4 px-6">{item.commodity}</td>
                <td className="py-4 px-6">{item.whr}</td>
                <td className="py-4 px-6">{item.dateAdded}</td>
                <td className="py-4 px-6">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      item.status === 'In Stock'
                        ? 'bg-green-100 text-green-800'
                        : item.status === 'Low Stock'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {item.status}
                  </span>
                </td>
              </motion.tr>
            ))}
            {currentData.length === 0 && (
              <tr>
                <td
                  colSpan="8"
                  className="text-center py-10 text-gray-500 font-medium text-lg"
                >
                  🚫 No inventory items found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </motion.div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-8 text-lg">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium"
        >
          ⬅ Previous
        </button>
        <span className="text-gray-700 font-semibold">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage(currentPage + 1)}
          className="px-5 py-2 bg-blue-200 hover:bg-blue-300 disabled:opacity-40 rounded-lg font-medium"
        >
          Next ➡
        </button>
      </div>

      {/* WHR Upload Modal */}
      <AnimatePresence>
        {showWHRModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowWHRModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-blue-800">Upload WHR</h2>
                <button
                  onClick={() => setShowWHRModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <FiX />
                </button>
              </div>

              {/* Modal Form */}
              <div className="space-y-6">
                {/* State */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={whrForm.state}
                    onChange={(e) => handleWhrFormChange('state', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                  >
                    <option value="">Select State</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Gujarat">Gujarat</option>
                  </select>
                </div>

                {/* Commodity */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Commodity
                  </label>
                  <select
                    value={whrForm.commodity}
                    onChange={(e) => handleWhrFormChange('commodity', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                  >
                    <option value="">Select Commodity</option>
                    <option value="Grains">Grains</option>
                    <option value="Pulses">Pulses</option>
                    <option value="Sweeteners">Sweeteners</option>
                    <option value="Oils">Oils</option>
                    <option value="Condiments">Condiments</option>
                  </select>
                </div>

                {/* Warehouse */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Warehouse
                  </label>
                  <select
                    value={whrForm.warehouse}
                    onChange={(e) => handleWhrFormChange('warehouse', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                  >
                    <option value="">Select Warehouse</option>
                    <option value="WH001">WH001</option>
                    <option value="WH002">WH002</option>
                    <option value="WH003">WH003</option>
                    <option value="WH004">WH004</option>
                  </select>
                </div>

                {/* Season */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Season
                  </label>
                  <select
                    value={whrForm.season}
                    onChange={(e) => handleWhrFormChange('season', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                  >
                    <option value="">Select Season</option>
                    <option value="Kharif">Kharif</option>
                    <option value="Rabi">Rabi</option>
                    <option value="Summer">Summer</option>
                  </select>
                </div>

                {/* Scheme */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Scheme
                  </label>
                  <select
                    value={whrForm.scheme}
                    onChange={(e) => handleWhrFormChange('scheme', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                  >
                    <option value="">Select Scheme</option>
                    <option value="PMFBY">PMFBY</option>
                    <option value="MNAIS">MNAIS</option>
                    <option value="Weather Based">Weather Based</option>
                  </select>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Upload File
                  </label>
                  <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:border-blue-500 transition">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <div className="text-5xl mb-2">📁</div>
                      <p className="text-gray-600">
                        {whrForm.file ? whrForm.file.name : 'Click to upload or drag file'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">CSV, XLS, or XLSX</p>
                    </label>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setShowWHRModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWhrSubmit}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
                >
                  <FiUploadCloud /> Upload
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
